/**
 * Pack Funnel Manager - Moon Moon Agency
 * Compose un pack produit générique (jusqu'à 3 étapes configurables + 1 étape offerte) via accordéons
 */

/*********************************************************************************************
 * 🛏️ PACK FUNNEL MANAGER
 */
const PackFunnelManager = {
	config: {
		// Pas de money_format thème exposé côté JS dans ce projet (le reste du thème
		// formate côté Liquid/SSR) — Intl reste autonome sans fetch ni dépendance.
		currencyFormatter: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }),
		currencyFormatterNoCents: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }),
		selectors: {
			root: '[data-pack-funnel]',
			accordion: '[data-pack-funnel-accordion]',
			stepProduct: '.mm-pack-funnel-step-product',
			card: '[data-pack-funnel-card]',
			cardImage: '.mm-pack-funnel-product-card-image',
			stepThumbnailImage: '.mm-pack-funnel-steps-image',
			stepThumbnailFallback: '[data-pack-step-thumbnail-fallback]',
			galleryMainSlider: '[data-main-slider]',
			galleryThumbnailSlider: '[data-thumbnail-slider]',
			galleryList: '[data-splide-list]',
			galleryArrows: '.mm-main-product-medias-splide-arrows',
			variantPicker: '[data-pack-variant-picker]',
			variantTrigger: '[data-pack-variant-trigger]',
			variantPanel: '[data-pack-variant-panel]',
			variantValue: '[data-pack-variant-value]',
			variantCurrent: '[data-pack-variant-current]',
			variantBadge: '[data-pack-variant-badge]',
			variantSwatch: '[data-pack-variant-swatch]',
			variantSwatchOption: '[data-pack-variant-swatch-value]',
			variantForced: '[data-pack-variant-forced]',
			variantForcedValue: '[data-pack-variant-forced-value]',
			variantMobileSheet: '[data-pack-variant-mobile-sheet]',
			variantMobileOverlay: '[data-pack-variant-mobile-overlay]',
			variantMobileOption: '[data-pack-variant-mobile-value]',
			variantMobileConfirm: '[data-pack-variant-mobile-confirm]',
			variantMobileClose: '[data-pack-variant-mobile-close]',
			sizeReference: '[data-pack-size-reference]',
			quantityStepper: '[data-pack-quantity-stepper]',
			quantityValue: '[data-pack-quantity-value]',
			quantityDecrease: '[data-pack-quantity-decrease]',
			quantityIncrease: '[data-pack-quantity-increase]',
			submitBtn: '[data-pack-funnel-submit]',
			recapSubtotal: '[data-pack-recap-subtotal]',
			recapSubtotalLabel: '[data-pack-recap-subtotal-label]',
			recapSavingsRow: '[data-pack-recap-savings-row]',
			recapSavings: '[data-pack-recap-savings]',
			recapNet: '[data-pack-recap-net]',
			recapCtaPrice: '[data-pack-recap-cta-price]',
			headerPrice: '[data-pack-header-price]',
			headerMarket: '[data-pack-header-market]',
			headerMarketPrice: '[data-pack-header-market-price]',
			headerPercentage: '[data-pack-header-percentage]',
			headerInstallments: '[data-pack-header-installments]',
			mobileBar: '[data-pack-funnel-mobile-bar]',
			mobileBarToggle: '[data-pack-funnel-mobile-toggle]',
			mobileBarOverlay: '[data-pack-funnel-mobile-overlay]',
			stepNumber: '.mm-pack-funnel-accordion-number',
			stepCompleteLabel: '[data-pack-step-complete-label]',
			submitMessage: '[data-pack-submit-message]',
			recap: '[data-pack-funnel-recap]',
			stepInteraction: '[data-pack-variant-value], [data-pack-variant-swatch-value], [data-pack-variant-mobile-confirm], [data-pack-quantity-decrease], [data-pack-quantity-increase]',
		},
		classes: {
			variantUnavailable: 'is-variant-unavailable',
			variantSwatchActive: 'is-active',
			sizeFiltered: 'is-size-filtered',
			stepCurrent: 'is-current',
			stepComplete: 'is-complete',
			stepPop: 'is-pop',
			stepLineFilled: 'is-filled',
		},
	},

	state: {
		packId: null,
		packTitle: null,
		packLabel: null,
		// { [productId]: { variants, variantMetafieldsById, sizeMap } } — sizeMap:
		// Map<optionPosition, Map<value, codes[]>>, absent si le produit n'a pas
		// d'option taille (cf. data-pack-size-map, émis seulement dans ce cas).
		variantCatalog: {},
		stepOrder: [], // identifiants d'étape dérivés du DOM, dans l'ordre d'affichage (étapes vides absentes)
		steps: {}, // { [stepId]: { allowMultiple, lines: [{ variantId, productId, price, compareAtPrice, marketPrice, quantity, variantResolved }] } }
		sizeMatching: { active: false, referenceSizeCodes: [], duoSizeCodes: [] },
		completedSteps: new Set(), // étapes passées en revue (coche à la place du numéro)
		canSubmit: false,
	},

	// Un objet `money` Liquid référencé directement (data-price, ou variant.price
	// dans le JSON de product.variants) sérialise la valeur en centimes — jamais
	// en euros. Tout le state interne (line.price/compareAtPrice) est tenu en
	// euros pour rester le format attendu par currencyFormatter.format(), donc
	// la conversion se fait une seule fois ici, à l'entrée du state.
	_toEuros(cents) {
		return (Number(cents) || 0) / 100;
	},

	/**
	 * Initialize the module
	 */
	init() {
		const root = window.getElement(this.config.selectors.root);
		if (!root) return; // Guard clause : sortie silencieuse si absent du DOM

		this.root = root;
		this.state.packId = root.dataset.packId || null;
		this.state.packTitle = root.dataset.packTitle || null;
		this.state.packLabel = root.dataset.packLabel || null;

		this._resolveStepOrder();
		this._initSelections();
		this._resolveVariantCatalog();
		this._initSizeMatching();
		this._applyDefaultQuantities();
		this._initSiblingPickersAvailability();
		// Le SSR présélectionne la taille de la variante par défaut, sans la
		// confronter à l'étape 1 : on aligne dès l'init sur une taille compatible.
		if (this.state.sizeMatching.active) this._reapplySizeFiltering();
		this._applyDeepLinkSelections();
		this._bindEvents();
		this._initStepRail();
		this._initQuantityDisplays();
		this._updateSubmitState();
		this._updateRecap();
		this._refreshVisibleSwatches();
		this._observeFooter();
		window.log('info', '🛏️ [PACK_FUNNEL]', 'Initialized', { stepOrder: this.state.stepOrder });
	},

	// Pastilles numérotées : « courante » quand l'étape croise le centre de
	// l'écran, « complétée » (coche) une fois dépassée, modifiée par l'acheteur
	// ou quand le récap est atteint. Toutes complétées → le trait se remplit.
	_initStepRail() {
		const { accordion, recap, stepInteraction } = this.config.selectors;
		const accordions = this.root.querySelectorAll(accordion);
		if (!accordions.length) return;

		this._setCurrentStep(this.state.stepOrder[0]);
		if (typeof IntersectionObserver !== 'function') return;

		const stepObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				const stepId = entry.target.dataset.step;
				this._setCurrentStep(stepId);
				this.state.stepOrder.slice(0, this.state.stepOrder.indexOf(stepId)).forEach((id) => this._markStepComplete(id));
			});
		}, { rootMargin: '-35% 0px -55% 0px' });
		accordions.forEach((el) => stepObserver.observe(el));

		const recapEl = this.root.querySelector(recap);
		if (recapEl) {
			const recapObserver = new IntersectionObserver(([entry]) => {
				// Récap déjà dépassé : saut direct en bas de page (touche Fin, ancre)
				if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
				this.state.stepOrder.forEach((id) => this._markStepComplete(id));
				recapObserver.disconnect();
			}, { rootMargin: '0px 0px -20% 0px' });
			recapObserver.observe(recapEl);
		}

		this.root.addEventListener('pointerdown', (e) => {
			const stepId = e.target.closest(accordion)?.dataset.step;
			if (stepId) this._setCurrentStep(stepId);
		});

		// Choisir un produit, une taille ou une quantité = étape traitée. Les
		// sélections par lien profond posent le state sans passer par ici.
		const markFromEvent = (e) => {
			const stepId = e.target.closest(accordion)?.dataset.step;
			if (stepId) this._markStepComplete(stepId);
		};
		this.root.addEventListener('change', markFromEvent);
		this.root.addEventListener('click', (e) => {
			if (e.target.closest(stepInteraction)) markFromEvent(e);
		});
	},

	_getStepElements(stepId) {
		const accordion = this.root.querySelector(`${this.config.selectors.accordion}[data-step="${stepId}"]`);
		return {
			accordion,
			number: accordion?.querySelector(this.config.selectors.stepNumber),
			label: accordion?.querySelector(this.config.selectors.stepCompleteLabel),
		};
	},

	_setCurrentStep(stepId) {
		this.state.stepOrder.forEach((id) => {
			this._getStepElements(id).number?.classList.toggle(this.config.classes.stepCurrent, id === stepId);
		});
	},

	_markStepComplete(stepId) {
		if (this.state.completedSteps.has(stepId)) return;
		this.state.completedSteps.add(stepId);

		const { number, label } = this._getStepElements(stepId);
		number?.classList.add(this.config.classes.stepComplete);
		if (label) label.hidden = false;

		if (this.state.completedSteps.size === this.state.stepOrder.length) this._celebrateSteps();
	},

	// Le trait se remplit de haut en bas, chaque pastille fait un « pop » à
	// l'arrivée du remplissage.
	_celebrateSteps() {
		const { stepPop, stepLineFilled } = this.config.classes;
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.state.stepOrder.forEach((stepId, index) => {
			const { accordion, number } = this._getStepElements(stepId);
			if (reduceMotion) {
				accordion?.classList.add(stepLineFilled);
				return;
			}
			setTimeout(() => {
				if (!number) return;
				number.classList.remove(stepPop);
				void number.offsetWidth;
				number.classList.add(stepPop);
			}, index * 250);
			setTimeout(() => accordion?.classList.add(stepLineFilled), 120 + index * 250);
		});
	},

	// Masque la barre mobile fixed dès que le footer entre en viewport (elle
	// flotterait sinon par-dessus). threshold 0 : dès le premier pixel visible.
	_observeFooter() {
		const bar = window.getElement(this.config.selectors.mobileBar);
		const footer = window.getElement('.shopify-section-group-footer-group');
		if (!bar) return;

		const syncBar = () => {
			const hidden = bar.classList.contains('is-footer-visible');
			bar.inert = hidden;
			document.documentElement.style.scrollPaddingBottom = !hidden && bar.offsetHeight ? `${bar.offsetHeight + 16}px` : '';
		};
		syncBar();
		window.addEventListener('resize', syncBar);
		if (!footer || typeof IntersectionObserver !== 'function') return;

		new IntersectionObserver((entries) => {
			entries.forEach((entry) => bar.classList.toggle('is-footer-visible', entry.isIntersecting));
			syncBar();
		}, { threshold: 0 }).observe(footer);
	},

	// Cartes cochées au SSR : leur swatch Splide peut avoir monté avant que le
	// layout soit stable (track largeur 0). rAF puis refresh() recalcule.
	_refreshVisibleSwatches() {
		requestAnimationFrame(() => {
			this.root.querySelectorAll(`${this.config.selectors.card} input:checked`).forEach((input) => {
				const stepProduct = input.closest(this.config.selectors.stepProduct);
				stepProduct?.querySelectorAll(this.config.selectors.variantSwatch).forEach((swatch) => {
					swatch._splide?.refresh();
				});
			});
		});
	},

	/**
	 * Builds stepOrder from the accordions actually present in the DOM (empty
	 * steps are never rendered server-side) — no numbers or identifiers
	 * hardcoded, so the funnel adapts to any pack configuration.
	 */
	_resolveStepOrder() {
		const accordions = window.getElements(this.config.selectors.accordion);
		this.state.stepOrder = Array.from(accordions).map((accordion) => accordion.dataset.step);

		this.state.stepOrder.forEach((stepId) => {
			const accordion = this.root.querySelector(`${this.config.selectors.accordion}[data-step="${stepId}"]`);
			this.state.steps[stepId] = {
				allowMultiple: accordion?.dataset.packAllowMultiple === 'true',
				lines: [],
			};
		});
	},

	/**
	 * Applique les variantes passées en query string (ex. lien "Découvrir
	 * l'offre" de MM-Section-Promo) — un paramètre `pack_<stepId>=<variantId>`
	 * par étape.
	 *
	 * La variante visée n'est PAS forcément la variante par défaut d'une carte :
	 * viser l'input par [data-variant-id] ne matchait donc que les tailles
	 * par défaut et ignorait silencieusement toutes les autres. On résout ici la
	 * variante dans le catalogue pour retrouver le PRODUIT qui la porte, on coche
	 * sa carte, puis on repositionne chacun de ses pickers sur les valeurs
	 * d'options de cette variante.
	 *
	 * Passe par _onCardSelect/_onVariantChange plutôt que de toucher au state
	 * directement : le filtrage de taille cross-étapes, le recap et l'état du CTA
	 * en dépendent. Appelé après _resolveVariantCatalog (le catalogue est requis)
	 * et après _initSizeMatching (une sélection en étape 1 relance le filtrage).
	 *
	 * Un paramètre absent, ou qui ne correspond à aucune variante connue, laisse
	 * la sélection SSR inchangée — fallback silencieux, jamais d'erreur.
	 */
	_applyDeepLinkSelections() {
		const params = new URLSearchParams(window.location.search);

		this.state.stepOrder.forEach((stepId) => {
			const variantId = params.get(`pack_${stepId}`);
			if (!variantId) return;

			const accordion = this.root.querySelector(`${this.config.selectors.accordion}[data-step="${stepId}"]`);
			if (!accordion) return;

			let targetProductId = null;
			let targetVariant = null;
			accordion.querySelectorAll(this.config.selectors.card).forEach((card) => {
				if (targetVariant) return;
				const catalog = this.state.variantCatalog[card.dataset.productId];
				const match = catalog?.variants.find((v) => String(v.id) === String(variantId));
				if (match) {
					targetProductId = card.dataset.productId;
					targetVariant = match;
				}
			});
			if (!targetVariant) return;

			const card = accordion.querySelector(`${this.config.selectors.card}[data-product-id="${targetProductId}"]`);
			const stepProduct = card?.closest(this.config.selectors.stepProduct);
			const pickers = Array.from(stepProduct?.querySelectorAll(this.config.selectors.variantPicker) || []);
			const { variantValue, variantSwatchOption } = this.config.selectors;
			const isOffered = pickers.every((picker) => {
				const value = targetVariant[`option${picker.dataset.optionPosition}`];
				return !value || Array.from(picker.querySelectorAll(`${variantValue}, ${variantSwatchOption}`)).some((el) => (el.dataset.packVariantValue ?? el.dataset.packVariantSwatchValue) === value);
			});
			if (!isOffered) return;

			const input = card?.querySelector('input');
			// Étape checkbox : jamais cochée par le lien, la présélection est celle
			// du chargement direct. Seuls ses pickers suivent la variante (taille).
			if (input && input.type !== 'checkbox' && !input.disabled && !input.checked) {
				input.checked = true;
				this._onCardSelect(input);
			}

			pickers.forEach((picker) => {
				const value = targetVariant[`option${picker.dataset.optionPosition}`];
				if (value && picker.dataset.selectedValue !== value) this._onVariantChange(picker, value);
			});
		});
	},

	/**
	 * Reads the cards checked by default (SSR) into the state. variantResolved
	 * mirrors the input's disabled attribute (posed server-side when
	 * variant.available is false, cf. mm-pack-funnel-product-card.liquid) —
	 * an admin-selected default product can itself be out of stock, so this
	 * can't just be hardcoded true like before _updateSubmitState relied on
	 * this flag to gate the CTA.
	 */
	_initSelections() {
		this.state.stepOrder.forEach((stepId) => {
			const input = this.root.querySelector(`[name="pack-step-${stepId}"]:checked`);
			if (!input) return;
			const card = input.closest(this.config.selectors.card);
			this.state.steps[stepId].lines.push({
				variantId: input.dataset.variantId,
				productId: card?.dataset.productId || null,
				price: this._toEuros(input.dataset.price),
				compareAtPrice: this._toEuros(input.dataset.compareAtPrice),
				marketPrice: this._toEuros(input.dataset.marketPrice),
				quantity: 1,
				variantResolved: !input.disabled,
			});
			if (this.state.steps[stepId].allowMultiple) this._updateStepThumbnail(stepId);
		});
	},

	/**
	 * Parses the inline variants/options/size-map JSON embedded on cards with
	 * a variant selector. Unlike before, a product can have several variable
	 * options (Dimensions + Pieds...) each with its own picker — the catalog
	 * keeps the raw variants array so a variant can be resolved from the
	 * COMBINATION of all pickers' current values (_resolveVariantFromSelections),
	 * not from a single value → variant lookup as when only one option varied.
	 */
	_resolveVariantCatalog() {
		this.root.querySelectorAll('[data-pack-variants]').forEach((script) => {
			const productId = script.dataset.packVariants;

			let variants;
			try {
				variants = JSON.parse(script.textContent);
			} catch (error) {
				window.log('warn', '🛏️ [PACK_FUNNEL]', 'Failed to parse variants JSON', { productId, error: error.message });
				return;
			}

			// Second JSON séparé, optionnel — pas fusionné dans product.variants
			// | json côté Liquid (ce filtre natif ne sérialise jamais les
			// metafields). Indexé par variant.id (toujours string ici, via
			// Object.entries), à caster en String() au lookup si comparé à un
			// variant.id natif (number, issu de product.variants | json).
			const metafieldsScript = this.root.querySelector(`[data-pack-variant-metafields="${productId}"]`);
			let variantMetafieldsById = new Map();
			if (metafieldsScript) {
				try {
					const metafieldsById = JSON.parse(metafieldsScript.textContent);
					variantMetafieldsById = new Map(Object.entries(metafieldsById));
				} catch (error) {
					window.log('warn', '🛏️ [PACK_FUNNEL]', 'Failed to parse variant metafields JSON', { productId, error: error.message });
				}
			}

			// Size map optionnel — absent si le produit n'a pas d'option taille
			// (cf. data-pack-size-map, snippets/mm-pack-funnel-product-card.liquid).
			// { [optionPosition]: { [value]: codes[] } } → Map<position, Map<value, codes[]>>.
			const sizeMapScript = this.root.querySelector(`[data-pack-size-map="${productId}"]`);
			let sizeMap = new Map();
			if (sizeMapScript) {
				try {
					const rawSizeMap = JSON.parse(sizeMapScript.textContent);
					Object.entries(rawSizeMap).forEach(([position, valueMap]) => {
						sizeMap.set(position, new Map(Object.entries(valueMap)));
					});
				} catch (error) {
					window.log('warn', '🛏️ [PACK_FUNNEL]', 'Failed to parse size map JSON', { productId, error: error.message });
				}
			}

			this.state.variantCatalog[productId] = { variants, variantMetafieldsById, sizeMap };
		});
	},

	/**
	 * Reads the pack-level size reference JSON (SSR state, cf.
	 * MM-Product-Pack-Funnel.liquid data-pack-size-reference) into
	 * state.sizeMatching. Fail-open by construction: hasSizeMatching false
	 * (pack without any custom.taille_standard anywhere) leaves `active`
	 * false, and every other size-matching method short-circuits on that flag
	 * — no filtering logic runs at all for such packs.
	 */
	_initSizeMatching() {
		const referenceScript = this.root.querySelector(this.config.selectors.sizeReference);
		window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Looking for size reference script', {
			selector: this.config.selectors.sizeReference,
			found: Boolean(referenceScript),
			rawTextContent: referenceScript?.textContent,
		});
		if (!referenceScript) return;

		try {
			const reference = JSON.parse(referenceScript.textContent);
			this.state.sizeMatching.active = Boolean(reference.hasSizeMatching);
			this.state.sizeMatching.referenceSizeCodes = reference.referenceSizeCodes || [];
			this.state.sizeMatching.duoSizeCodes = reference.duoSizeCodes || [];
		} catch (error) {
			window.log('warn', '🛏️ [PACK_FUNNEL]', 'Failed to parse size reference JSON', { error: error.message, rawTextContent: referenceScript.textContent });
		}

		window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Size matching initialized', {
			active: this.state.sizeMatching.active,
			referenceSizeCodes: this.state.sizeMatching.referenceSizeCodes,
			variantCatalog: Object.fromEntries(
				Object.entries(this.state.variantCatalog).map(([productId, catalog]) => [
					productId,
					{ sizeMapPositions: Array.from(catalog.sizeMap.keys()), sizeMapEntries: Object.fromEntries(Array.from(catalog.sizeMap.entries()).map(([pos, map]) => [pos, Object.fromEntries(map)])) },
				])
			),
		});
	},

	/**
	 * Pure function: which values of a product's size option are compatible
	 * with the current reference codes. Mirrors EXACTLY the Liquid SSR rule
	 * (snippets/mm-pack-funnel-step-product.liquid) — a value with no codes
	 * recorded (product/variant never got custom.taille_standard) is always
	 * compatible (fail-open), an empty referenceSizeCodes never restricts
	 * anything (guarded by the `active` flag before this is ever called).
	 */
	_getCompatibleSizeValues(sizeValueMap, referenceSizeCodes) {
		const compatibleValues = [];
		sizeValueMap.forEach((codes, value) => {
			if (!codes || codes.length === 0 || codes.some((code) => referenceSizeCodes.includes(code))) {
				compatibleValues.push(value);
			}
		});
		return compatibleValues;
	},

	_bindEvents() {
		this.root.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.variantTrigger);
			if (trigger) {
				const picker = trigger.closest(this.config.selectors.variantPicker);
				const isDropdown = Boolean(picker?.querySelector(this.config.selectors.variantPanel));
				const mobileSheet = picker?.querySelector(this.config.selectors.variantMobileSheet);
				if (isDropdown && mobileSheet && window.isMobile()) {
					this._openMobileVariantSheet(picker);
				} else {
					this._toggleVariantPanel(picker);
				}
			}

			const variantOption = e.target.closest(this.config.selectors.variantValue);
			if (variantOption) this._onVariantChange(variantOption.closest(this.config.selectors.variantPicker), variantOption.dataset.packVariantValue);

			const swatchOption = e.target.closest(this.config.selectors.variantSwatchOption);
			if (swatchOption) this._onVariantChange(swatchOption.closest(this.config.selectors.variantPicker), swatchOption.dataset.packVariantSwatchValue);

			const mobileOption = e.target.closest(this.config.selectors.variantMobileOption);
			if (mobileOption) this._onMobileVariantPick(mobileOption);

			const mobileConfirm = e.target.closest(this.config.selectors.variantMobileConfirm);
			if (mobileConfirm) this._confirmMobileVariantSheet(mobileConfirm.closest(this.config.selectors.variantMobileSheet));

			if (e.target.closest(this.config.selectors.variantMobileClose)) this._closeMobileVariantSheet(e.target.closest(this.config.selectors.variantMobileSheet));

			if (e.target.closest(this.config.selectors.variantMobileOverlay)) this._closeMobileVariantSheet(e.target.closest(this.config.selectors.variantPicker)?.querySelector(this.config.selectors.variantMobileSheet));

			const increaseBtn = e.target.closest(this.config.selectors.quantityIncrease);
			if (increaseBtn) this._onQuantityChange(increaseBtn.closest(this.config.selectors.quantityStepper), 1);

			const decreaseBtn = e.target.closest(this.config.selectors.quantityDecrease);
			if (decreaseBtn) this._onQuantityChange(decreaseBtn.closest(this.config.selectors.quantityStepper), -1);

			if (e.target.closest(this.config.selectors.submitBtn)) this._addPackToCart();

			if (e.target.closest(this.config.selectors.mobileBarToggle)) this._toggleMobileBar();

			if (e.target.closest(this.config.selectors.mobileBarOverlay)) this._closeMobileBar();
		});

		this.root.addEventListener('change', (e) => {
			if (!e.target.matches(`${this.config.selectors.card} input`)) return;
			const stepState = this.state.steps[e.target.name.replace('pack-step-', '')];
			if (stepState) stepState.quantityTouched = true;
			this._onCardSelect(e.target, true);
		});

		// Sur document (pas this.root) : un clic en dehors de la section entière
		// (header, galerie médias...) doit aussi refermer un panel ouvert. Posé
		// une seule fois au chargement — pas de risque de fermeture immédiate au
		// clic d'ouverture, ce clic est un événement distinct déjà géré ci-dessus.
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.variantPicker)) return;
			this._closeAllVariantPanels();
		});

		document.addEventListener('keydown', (e) => {
			if (e.key !== 'Escape') return;
			const openSheet = this.root.querySelector(`${this.config.selectors.variantMobileSheet}.is-open`);
			if (openSheet) this._closeMobileVariantSheet(openSheet);
			this._closeAllVariantPanels();
		});
	},

	// Ouvre le bottom sheet mobile du picker dropdown : la sélection y reste
	// "en attente" (dataset.pendingValue) tant que le CTA Sélectionner n'a pas
	// été cliqué, cf. {% doc %} de mm-pack-funnel-variant-picker.liquid.
	_openMobileVariantSheet(picker) {
		const sheet = picker?.querySelector(this.config.selectors.variantMobileSheet);
		const overlay = picker?.querySelector(this.config.selectors.variantMobileOverlay);
		if (!sheet) return;

		sheet.dataset.pendingValue = picker.dataset.selectedValue || '';
		clearTimeout(sheet._hideTimer);
		sheet.hidden = false;
		// requestAnimationFrame : le retrait de [hidden] et l'ajout de .is-open
		// doivent être deux frames distinctes pour que la transition CSS
		// (transform) joue, sinon le navigateur les fusionne et le sheet
		// apparaît déjà ouvert sans animation.
		requestAnimationFrame(() => {
			sheet.classList.add('is-open');
			overlay?.classList.add('is-open');
			// focus après .is-open : tant que le sheet est en visibility:hidden, il n'est pas focusable
			sheet.querySelector(this.config.selectors.variantMobileClose)?.focus();
		});

		this._setMobileVariantOptionsState(sheet, sheet.dataset.pendingValue);
	},

	_closeMobileVariantSheet(sheet) {
		if (!sheet) return;
		const picker = sheet.closest(this.config.selectors.variantPicker);
		const overlay = picker?.querySelector(this.config.selectors.variantMobileOverlay);

		sheet.classList.remove('is-open');
		overlay?.classList.remove('is-open');
		// [hidden] retiré après la transition de sortie (.42s dans mm-pack-funnel.css),
		// sinon display:none coupe l'animation de fermeture net.
		clearTimeout(sheet._hideTimer);
		sheet._hideTimer = setTimeout(() => { sheet.hidden = true; }, 450);
		delete sheet.dataset.pendingValue;

		picker?.querySelector(this.config.selectors.variantTrigger)?.focus();
	},

	// Marque juste la ligne cliquée comme sélection en attente — n'appelle PAS
	// _onVariantChange ici : seul le clic sur Sélectionner doit résoudre/
	// appliquer la variante.
	_onMobileVariantPick(optionEl) {
		if (optionEl.getAttribute('aria-disabled') === 'true') return;
		const sheet = optionEl.closest(this.config.selectors.variantMobileSheet);
		if (!sheet) return;

		sheet.dataset.pendingValue = optionEl.dataset.packVariantMobileValue;
		this._setMobileVariantOptionsState(sheet, sheet.dataset.pendingValue);
	},

	_setMobileVariantOptionsState(sheet, selectedValue) {
		sheet.querySelectorAll(this.config.selectors.variantMobileOption).forEach((optionEl) => {
			const isSelected = optionEl.dataset.packVariantMobileValue === selectedValue;
			optionEl.classList.toggle('is-selected', isSelected);
			optionEl.setAttribute('aria-selected', String(isSelected));
		});
	},

	// Seul point d'application réelle du choix mobile : délègue entièrement à
	// _onVariantChange (résolution de variante, recap, pickers voisins, size
	// matching) pour ne dupliquer aucune de cette logique.
	_confirmMobileVariantSheet(sheet) {
		if (!sheet) return;
		const picker = sheet.closest(this.config.selectors.variantPicker);
		const value = sheet.dataset.pendingValue;
		if (picker && value) this._onVariantChange(picker, value);

		this._closeMobileVariantSheet(sheet);
	},

	_closeAllVariantPanels() {
		this.root.querySelectorAll(`${this.config.selectors.variantPicker}.is-open`).forEach((picker) => this._toggleVariantPanel(picker));
	},

	// Barre récap/CTA fixed mobile, ouverte/fermée comme un accordéon simple
	// (un seul bouton fait office de toggle +/×, cf. data-open sur le wrapper).
	_toggleMobileBar() {
		const bar = window.getElement(this.config.selectors.mobileBar);
		if (!bar) return;
		this._setMobileBarOpen(bar.dataset.open !== 'true');
	},

	// Fermeture explicite (overlay cliqué) — jamais un toggle, pour ne pas
	// rouvrir la barre si l'état était déjà fermé.
	_closeMobileBar() {
		this._setMobileBarOpen(false);
	},

	_setMobileBarOpen(isOpen) {
		const bar = window.getElement(this.config.selectors.mobileBar);
		const toggle = bar?.querySelector(this.config.selectors.mobileBarToggle);
		if (!bar || !toggle) return;

		bar.dataset.open = String(isOpen);
		toggle.setAttribute('aria-expanded', String(isOpen));
	},

	// is-open posé sur le picker et son panel simultanément, aria-expanded sur
	// le trigger — composant isolé de .mm-dd/mm-size-selector.js (cf. {% doc %}
	// du snippet), ne s'applique qu'au dropdown (le swatch n'a pas de panel).
	_toggleVariantPanel(picker) {
		if (!picker) return;
		const trigger = picker.querySelector(this.config.selectors.variantTrigger);
		const panel = picker.querySelector(this.config.selectors.variantPanel);
		if (!trigger || !panel) return;
		const isOpen = picker.classList.contains('is-open');
		const focusInside = isOpen && panel.contains(document.activeElement);

		picker.classList.toggle('is-open', !isOpen);
		panel.classList.toggle('is-open', !isOpen);
		trigger.setAttribute('aria-expanded', String(!isOpen));
		if (focusInside) trigger.focus({ preventScroll: true });
	},

	/**
	 * Reads every picker's currently selected value inside one product's
	 * .mm-pack-funnel-step-product wrapper, keyed by option position. Needed
	 * to resolve a variant when a product has 2+ variable options (e.g.
	 * Dimensions + Pieds): a single value is no longer enough to identify a
	 * unique variant, cf. memory project_pack_funnel_size_matching.
	 */
	_getCurrentOptionSelections(stepProduct) {
		const selections = {};
		stepProduct.querySelectorAll(this.config.selectors.variantPicker).forEach((sibling) => {
			const position = sibling.dataset.optionPosition;
			const selectedValue = sibling.dataset.selectedValue;
			if (position && selectedValue) selections[position] = selectedValue;
		});
		return selections;
	},

	/**
	 * Resolves the variant matching ALL known option selections simultaneously
	 * (option1/2/3 by position), replacing the old single-value
	 * variantsByValue.get(value) lookup that only worked because at most one
	 * option ever varied. Returns undefined if no variant matches — e.g. an
	 * impossible Dimensions+Pieds combination.
	 */
	_resolveVariantFromSelections(productId, selections) {
		const catalog = this.state.variantCatalog[productId];
		if (!catalog) return undefined;

		return catalog.variants.find((variant) =>
			Object.entries(selections).every(([position, value]) => variant[`option${position}`] === value)
		);
	},

	/**
	 * SSR renders every picker at its default value (selected_or_first_available_variant)
	 * without knowing about the other pickers on the same product — so a
	 * Dimensions value with zero matching Couleur variant shows up clickable
	 * on first paint. Runs the same cross-picker availability check as
	 * _updateSiblingPickersAvailability for every product card, with no
	 * "changed" picker to exclude (null: nothing is an explicit active pick yet).
	 */
	_initSiblingPickersAvailability() {
		this.root.querySelectorAll(this.config.selectors.stepProduct).forEach((stepProduct) => {
			const card = stepProduct.querySelector(this.config.selectors.card);
			const productId = card?.dataset.productId;
			if (!productId || !this.state.variantCatalog[productId]) return;

			this._updateSiblingPickersAvailability(stepProduct, productId, null);
		});
	},

	/**
	 * After a picker's value changes, re-evaluates every OTHER picker of the
	 * same product: for each of their candidate values, substitute it into
	 * the current selections and check whether a variant exists and is
	 * available. Greys out (aria-disabled) values that would lead to an
	 * impossible/unavailable combination — same visual treatment as size
	 * incompatibility (cf. mm-pack-funnel.css [aria-disabled="true"]), never
	 * the picker that just changed (that choice is an explicit active pick).
	 */
	_updateSiblingPickersAvailability(stepProduct, productId, changedOptionPosition) {
		const selections = this._getCurrentOptionSelections(stepProduct);

		stepProduct.querySelectorAll(this.config.selectors.variantPicker).forEach((sibling) => {
			const siblingPosition = sibling.dataset.optionPosition;
			if (!siblingPosition || siblingPosition === changedOptionPosition) return;

			const isDropdown = Boolean(sibling.querySelector(this.config.selectors.variantPanel));
			const optionSelector = isDropdown ? this.config.selectors.variantValue : this.config.selectors.variantSwatchOption;
			const valueAttr = isDropdown ? 'packVariantValue' : 'packVariantSwatchValue';
			// Le bottom sheet mobile (dropdown uniquement — le swatch photo n'a pas
			// d'équivalent mobile séparé) duplique les mêmes valeurs sous
			// data-pack-variant-mobile-value : sans ce lookup, il resterait toujours
			// cliquable/non grisé même quand le panel desktop l'est correctement.
			const mobileOptionByValue = new Map();
			if (isDropdown) {
				sibling.querySelectorAll(this.config.selectors.variantMobileOption).forEach((mobileEl) => {
					mobileOptionByValue.set(mobileEl.dataset.packVariantMobileValue, mobileEl);
				});
			}

			sibling.querySelectorAll(optionSelector).forEach((optionEl) => {
				const candidateValue = optionEl.dataset[valueAttr];
				const candidateSelections = { ...selections, [siblingPosition]: candidateValue };
				const candidateVariant = this._resolveVariantFromSelections(productId, candidateSelections);
				const isReachable = Boolean(candidateVariant?.available);
				// setAttribute/removeAttribute explicites, pas toggleAttribute(name,
				// bool) qui pose l'attribut sans valeur (aria-disabled="") — ne
				// matche jamais le sélecteur CSS [aria-disabled="true"].
				const mobileEl = mobileOptionByValue.get(candidateValue);
				if (isReachable) {
					optionEl.removeAttribute('aria-disabled');
					mobileEl?.removeAttribute('aria-disabled');
				} else {
					optionEl.setAttribute('aria-disabled', 'true');
					mobileEl?.setAttribute('aria-disabled', 'true');
				}
			});
		});
	},

	/**
	 * Resolves a variant change on a single card's own picker (dropdown or
	 * swatch — same resolution logic, only the visual reflect differs). Each
	 * product resolves independently: no cross-product synchronization,
	 * except for its OWN sibling pickers (combination availability, cf.
	 * _updateSiblingPickersAvailability) and, if this picker drives step 1's
	 * size option, the cross-step size filtering (cf. _reapplySizeFiltering).
	 */
	_onVariantChange(picker, value) {
		if (!picker) return;
		// Le picker est un frère de la carte (tous deux enfants de
		// .mm-pack-funnel-step-product), jamais un descendant : closest() depuis
		// le picker ne peut pas remonter vers un frère, il faut repasser par le
		// wrapper commun puis redescendre.
		const stepProduct = picker.closest(this.config.selectors.stepProduct);
		const card = stepProduct?.querySelector(this.config.selectors.card);
		const productId = card?.dataset.productId;
		const accordion = picker.closest(this.config.selectors.accordion);
		const stepId = accordion?.dataset.step;
		const stepState = this.state.steps[stepId];
		const catalog = this.state.variantCatalog[productId];
		if (!stepState || !catalog || !card || !stepProduct) return;

		picker.dataset.selectedValue = value;

		const isDropdown = Boolean(picker.querySelector(this.config.selectors.variantPanel));

		if (isDropdown) {
			const currentEl = picker.querySelector(this.config.selectors.variantCurrent);
			if (currentEl) currentEl.textContent = value;
			let selectedOption = null;
			picker.querySelectorAll(this.config.selectors.variantValue).forEach((option) => {
				const isSelected = option.dataset.packVariantValue === value;
				option.classList.toggle('is-selected', isSelected);
				option.setAttribute('aria-selected', String(isSelected));
				if (isSelected) selectedOption = option;
			});
			// badge (mm_badges) : reflète dans le trigger celui de l'option choisie —
			// même pattern que mm-size-selector.js (data-dd-badge)
			const badgeEl = picker.querySelector(this.config.selectors.variantBadge);
			if (badgeEl) {
				const optionBadge = selectedOption?.querySelector('.mm-label');
				badgeEl.innerHTML = optionBadge ? optionBadge.outerHTML : '';
			}
			// Ne referme que si le panel desktop était réellement ouvert — un
			// changement déclenché depuis le sheet mobile (_confirmMobileVariantSheet)
			// n'a jamais ouvert ce panel, un toggle inconditionnel l'ouvrirait ici
			// par erreur au lieu de le laisser fermé.
			if (picker.classList.contains('is-open')) this._toggleVariantPanel(picker);
		} else {
			picker.querySelectorAll(this.config.selectors.variantSwatchOption).forEach((option) => {
				const isSelected = option.dataset.packVariantSwatchValue === value;
				option.classList.toggle(this.config.classes.variantSwatchActive, isSelected);
				option.setAttribute('aria-pressed', String(isSelected));
			});
			// Photo swatch (carte Couleur) : même span que le trigger dropdown,
			// affiché à côté du titre uniquement en mobile (cf. mm-pack-funnel.css).
			const currentEl = picker.querySelector(this.config.selectors.variantCurrent);
			if (currentEl) currentEl.textContent = value;
		}

		const selections = this._getCurrentOptionSelections(stepProduct);
		const variant = this._resolveVariantFromSelections(productId, selections);
		const isAvailable = Boolean(variant?.available);
		card.classList.toggle(this.config.classes.variantUnavailable, !isAvailable);

		const line = stepState.lines.find((l) => l.productId === productId);
		if (line) {
			line.variantResolved = isAvailable;
			// String(variant.id) : la clé JSON de variantMetafieldsById vient
			// de Object.entries côté JS (toujours string), alors que
			// variant.id ici est un number natif issu de product.variants |
			// json — sans le cast, .get(variant.id) ne matche jamais.
			const marketPriceCents = variant ? catalog.variantMetafieldsById.get(String(variant.id))?.new_price || 0 : 0;
			// Prix de la combinaison choisie affiché même en rupture : sinon la
			// carte garde le prix SSR (1re variante si aucune n'est disponible).
			if (variant) {
				this._updateCardPriceDisplay(card, this._toEuros(variant.price), this._toEuros(variant.compare_at_price), this._toEuros(marketPriceCents));
			}
			if (isAvailable) {
				line.variantId = variant.id;
				line.price = this._toEuros(variant.price);
				line.compareAtPrice = this._toEuros(variant.compare_at_price);
				line.marketPrice = this._toEuros(marketPriceCents);
				// _onCardSelect relit ces data-* : sans cette synchro, recocher la
				// carte reprendrait les prix SSR de la variante par défaut.
				const input = card.querySelector('input');
				if (input) {
					input.dataset.variantId = variant.id;
					input.dataset.price = variant.price;
					input.dataset.compareAtPrice = variant.compare_at_price || '';
					input.dataset.marketPrice = marketPriceCents;
				}
			}
		}

		this._updateSiblingPickersAvailability(stepProduct, productId, picker.dataset.optionPosition);

		window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'onVariantChange', {
			stepId,
			productId,
			value,
			pickerIsSizeOption: picker.dataset.isSizeOption,
			pickerOptionPosition: picker.dataset.optionPosition,
		});

		if (picker.dataset.isSizeOption === 'true' && stepId === 'step_1') {
			const sizeMap = catalog.sizeMap.get(picker.dataset.optionPosition);
			this.state.sizeMatching.referenceSizeCodes = sizeMap?.get(value) || [];
			// `active` ne peut pas rester figé sur la valeur lue au SSR
			// (data-pack-size-reference) : si la variante par défaut de step_1
			// au premier rendu n'avait pas taille_standard renseigné,
			// hasSizeMatching valait false côté Liquid — mais un choix explicite
			// ici peut très bien porter des codes non vides. Sans ce recalcul,
			// _reapplySizeFiltering sortait immédiatement (guard `active`) et
			// aucun clic ultérieur ne pouvait plus jamais activer le matching.
			this.state.sizeMatching.active = this.state.sizeMatching.referenceSizeCodes.length > 0;
			window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Step 1 size picker changed — new reference codes', {
				value,
				sizeMapPositions: Array.from(catalog.sizeMap.keys()),
				sizeMapForThisPosition: sizeMap ? Object.fromEntries(sizeMap) : null,
				newReferenceSizeCodes: this.state.sizeMatching.referenceSizeCodes,
				newActive: this.state.sizeMatching.active,
			});
			this._reapplySizeFiltering();
		}

		this._updateSubmitState();
		this._updateRecap();
	},

	/**
	 * Re-applies cross-step size filtering after step 1's size changes:
	 * re-greys following steps' size pickers, hides products with zero
	 * compatible value, and auto-selects the first compatible value wherever
	 * the current one no longer matches. Clears everything if size matching
	 * becomes inactive.
	 */
	_reapplySizeFiltering() {
		this._applyDefaultQuantities(true);
		window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', '_reapplySizeFiltering called', {
			active: this.state.sizeMatching.active,
			referenceSizeCodes: this.state.sizeMatching.referenceSizeCodes,
		});
		if (!this.state.sizeMatching.active) {
			this._clearSizeFiltering();
			return;
		}

		const { referenceSizeCodes } = this.state.sizeMatching;

		this.state.stepOrder.forEach((stepId) => {
			if (stepId === 'step_1') return;

			const accordion = this.root.querySelector(`${this.config.selectors.accordion}[data-step="${stepId}"]`);
			if (!accordion) return;

			accordion.querySelectorAll(this.config.selectors.stepProduct).forEach((stepProduct) => {
				const card = stepProduct.querySelector(this.config.selectors.card);
				const productId = card?.dataset.productId;
				const catalog = this.state.variantCatalog[productId];

				if (!card || !catalog) {
					window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Skipping product: no card or no catalog entry', { stepId, productId, hasCard: Boolean(card), hasCatalog: Boolean(catalog) });
					return;
				}

				const sizePicker = stepProduct.querySelector(`${this.config.selectors.variantPicker}[data-is-size-option="true"]`);
				const sizePosition = sizePicker?.dataset.optionPosition;
				const sizeValueMap = sizePosition ? catalog.sizeMap.get(sizePosition) : undefined;

				// Produit sans option taille détectée (sizeValueMap absent) :
				// fail-open, jamais filtré ni re-grisé par ce mécanisme.
				if (!sizeValueMap) {
					window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Skipping product: no size option/sizeMap found (fail-open)', {
						stepId,
						productId,
						hasSizePicker: Boolean(sizePicker),
						sizePosition,
						sizeMapPositions: Array.from(catalog.sizeMap.keys()),
					});
					return;
				}

				const compatibleValues = this._getCompatibleSizeValues(sizeValueMap, referenceSizeCodes);

				window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Product size compatibility computed', {
					stepId,
					productId,
					sizeValueMapEntries: Object.fromEntries(sizeValueMap),
					referenceSizeCodes,
					compatibleValues,
					currentSelectedValue: sizePicker?.dataset.selectedValue,
				});

				if (compatibleValues.length === 0) {
					window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Filtering out product: zero compatible values', { stepId, productId });
					this._filterOutProduct(stepId, stepProduct, productId);
					return;
				}

				stepProduct.classList.remove(this.config.classes.sizeFiltered);

				if (sizePicker) {
					// Miroir de la règle SSR force_hidden (cf. mm-pack-funnel-step-product.liquid) :
					// 1 seule valeur compatible => picker caché (rien à choisir), 2+ =>
					// visible. Sans ce reflect, un picker caché au SSR restait caché
					// pour toujours même après un clic faisant passer compatibleValues
					// à 2+ alors qu'un vrai choix devient possible.
					const isForced = compatibleValues.length <= 1;
					sizePicker.hidden = isForced;

					// Bloc texte de repli (frère du picker, cf. data-pack-variant-forced
					// dans mm-pack-funnel-step-product.liquid) : visible uniquement
					// quand le picker est caché, avec la taille forcée à jour — sinon
					// il restait figé sur l'état calculé au SSR après un changement de
					// taille en étape 1.
					const forcedEl = stepProduct.querySelector(this.config.selectors.variantForced);
					if (forcedEl) {
						forcedEl.hidden = !isForced;
						if (isForced) {
							const forcedValueEl = forcedEl.querySelector(this.config.selectors.variantForcedValue);
							if (forcedValueEl) forcedValueEl.textContent = compatibleValues[0];
						}
					}

					const isDropdown = sizePicker.querySelector(this.config.selectors.variantPanel);
					const optionSelector = isDropdown ? this.config.selectors.variantValue : this.config.selectors.variantSwatchOption;
					const valueAttr = isDropdown ? 'packVariantValue' : 'packVariantSwatchValue';

					sizePicker.querySelectorAll(optionSelector).forEach((optionEl) => {
						const isCompatible = compatibleValues.includes(optionEl.dataset[valueAttr]);
						// toggleAttribute(name, true) pose l'attribut SANS valeur
						// (aria-disabled=""), qui ne matche jamais le sélecteur CSS
						// [aria-disabled="true"] — d'où le grisage silencieusement
						// absent malgré le calcul correct. setAttribute/removeAttribute
						// explicites pour poser la vraie valeur "true".
						if (isCompatible) {
							optionEl.removeAttribute('aria-disabled');
						} else {
							optionEl.setAttribute('aria-disabled', 'true');
						}
					});

					// Valeur courante incompatible (ou jamais choisie) : auto-sélectionne
					// la 1re valeur compatible plutôt que réinitialiser à vide — passe
					// par _onVariantChange pour bénéficier du reflect visuel, de la
					// résolution de variante et de la mise à jour du prix déjà en place.
					if (!compatibleValues.includes(sizePicker.dataset.selectedValue)) {
						const nextValue = compatibleValues[0];
						window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Auto-selecting compatible value', { stepId, productId, previousValue: sizePicker.dataset.selectedValue, nextValue });
						this._onVariantChange(sizePicker, nextValue);
					}
				}
			});
		});

		this._updateSubmitState();
		this._updateRecap();
	},

	/**
	 * Removes every grey-out/hide left by a previous size filtering pass —
	 * called when size matching becomes inactive (e.g. step 1's product
	 * changes to one without custom.taille_standard). Existing selections are
	 * left untouched: they simply become valid again, nothing to reset here.
	 */
	_clearSizeFiltering() {
		this.state.stepOrder.forEach((stepId) => {
			if (stepId === 'step_1') return;

			const accordion = this.root.querySelector(`${this.config.selectors.accordion}[data-step="${stepId}"]`);
			if (!accordion) return;

			accordion.querySelectorAll(this.config.selectors.stepProduct).forEach((stepProduct) => {
				stepProduct.classList.remove(this.config.classes.sizeFiltered);
				const sizePicker = stepProduct.querySelector(`${this.config.selectors.variantPicker}[data-is-size-option="true"]`);
				if (sizePicker) sizePicker.hidden = false;
				const forcedEl = stepProduct.querySelector(this.config.selectors.variantForced);
				if (forcedEl) forcedEl.hidden = true;
				sizePicker?.querySelectorAll(`${this.config.selectors.variantValue}, ${this.config.selectors.variantSwatchOption}`).forEach((optionEl) => {
					optionEl.removeAttribute('aria-disabled');
				});
			});
		});

		this._updateSubmitState();
		this._updateRecap();
	},

	/**
	 * Hides a product whose size became fully incompatible (règle 3) and, if
	 * it was selected, clears that selection from state first — a filtered
	 * product must never leave a ghost line in the cart recap.
	 */
	_filterOutProduct(stepId, stepProduct, productId) {
		this._resetProductSelection(stepId, stepProduct, productId);
		stepProduct.classList.add(this.config.classes.sizeFiltered);
	},

	/**
	 * Unchecks a product's input (DOM) and removes its line from state
	 * together, so _updateRecap/_updateSubmitState stay consistent.
	 */
	_resetProductSelection(stepId, stepProduct, productId) {
		const stepState = this.state.steps[stepId];
		if (!stepState) return;

		const lineIndex = stepState.lines.findIndex((line) => line.productId === productId);
		if (lineIndex === -1) return;

		stepState.lines.splice(lineIndex, 1);
		const input = stepProduct.querySelector(`${this.config.selectors.card} input`);
		if (input) input.checked = false;
		this._updateQuantityDisplay(productId);
		this._updateStepThumbnail(stepId);
	},

	// Le compare-at-price et le market price sont toujours rendus côté serveur
	// (jamais créés/détruits en JS), juste masqués via `hidden` quand ils n'ont
	// pas lieu d'être — cf. {% doc %} du snippet. Le JS ne fait donc que
	// basculer visibilité et texte, jamais leur présence dans le DOM.
	_updateCardPriceDisplay(card, price, compareAtPrice, marketPrice) {
		const priceEl = card.querySelector('[data-pack-card-price]');
		if (priceEl) priceEl.textContent = this._formatMoney(price);

		const comparePriceEl = card.querySelector('[data-pack-card-compare-price]');
		if (comparePriceEl) {
			const hasDiscount = compareAtPrice > price;
			comparePriceEl.hidden = !hasDiscount;
			if (hasDiscount) {
				const strikethrough = comparePriceEl.querySelector('s');
				if (strikethrough) strikethrough.textContent = this._formatMoney(compareAtPrice);
			}
		}

		// market-row (prix barré) et le badge % sont deux frères indépendants
		// (le prix courant s'affiche entre les deux dans le HTML) : chacun a
		// son propre hidden, aucun n'hérite du hidden de l'autre.
		const marketRowEl = card.querySelector('[data-pack-card-market-row]');
		const percentageEl = card.querySelector('[data-pack-card-market-percentage]');
		if (marketPrice !== undefined) {
			const hasMarketPrice = marketPrice > price;
			if (marketRowEl) marketRowEl.hidden = !hasMarketPrice;
			if (percentageEl) percentageEl.hidden = !hasMarketPrice;

			if (hasMarketPrice) {
				const marketPriceEl = marketRowEl?.querySelector('[data-pack-card-market-price]');
				if (marketPriceEl) marketPriceEl.textContent = this._formatMoney(marketPrice);

				const percentageTemplate = percentageEl?.dataset.percentageTemplate;
				if (percentageEl && percentageTemplate) {
					const percentage = Math.round(((marketPrice - price) / marketPrice) * 100);
					const percentageTextEl = percentageEl.querySelector('.mm-label-text');
					if (percentageTextEl) percentageTextEl.textContent = percentageTemplate.replace('__PERCENTAGE__', percentage);
				}
			}
		}
	},

	// Le CTA reste désactivé tant qu'une ligne porte une variante indisponible
	// — que ce soit la sélection par défaut au chargement (_initSelections) ou
	// une variante devenue indisponible après un choix explicite sur un picker
	// (_onVariantChange) — filet de sécurité contre un variant_id invalide
	// envoyé au panier.
	// Deux instances du CTA dans le DOM (flux + barre fixed mobile, cf.
	// MM-Product-Pack-Funnel.liquid) : querySelectorAll pour que les deux
	// restent synchronisées, jamais getElement (ne toucherait que la 1ère).
	_updateSubmitState() {
		const submitBtns = window.getElements(this.config.selectors.submitBtn);
		if (!submitBtns.length) return;
		const unavailableStepId = this.state.stepOrder.find((stepId) => this.state.steps[stepId].lines.some((line) => line.variantResolved === false));
		const emptyStepId = this.state.stepOrder.find((stepId) => this.state.steps[stepId].allowMultiple && !this.state.steps[stepId].lines.length);
		this.state.canSubmit = !unavailableStepId && !emptyStepId;
		submitBtns.forEach((submitBtn) => submitBtn.setAttribute('aria-disabled', String(!this.state.canSubmit)));
		this._updateSubmitMessage(unavailableStepId, emptyStepId);
	},

	_updateSubmitMessage(unavailableStepId, emptyStepId) {
		const stepId = unavailableStepId || emptyStepId;
		const templateKey = unavailableStepId ? 'unavailableTemplate' : 'requiredTemplate';
		const stepLabel = stepId ? this._getStepElements(stepId).accordion?.dataset.stepLabel || '' : '';
		window.getElements(this.config.selectors.submitMessage).forEach((messageEl) => {
			messageEl.textContent = stepId ? (messageEl.dataset[templateKey] || '').replace('__STEP__', stepLabel) : '';
		});
	},

	_getSleepersCount() {
		const { referenceSizeCodes, duoSizeCodes } = this.state.sizeMatching;
		return referenceSizeCodes.some((code) => duoSizeCodes.includes(code)) ? 2 : 1;
	},

	_applyDefaultQuantities(animate = false) {
		const sleepers = this._getSleepersCount();
		Object.values(this.state.steps).forEach((stepState) => {
			if (!stepState.allowMultiple || stepState.quantityTouched) return;
			stepState.lines.forEach((line) => {
				const stepper = this.root.querySelector(`${this.config.selectors.quantityStepper}[data-product-id="${line.productId}"]`);
				line.quantity = Math.min(sleepers, Number(stepper?.dataset.maxQty) || Infinity);
				this._updateQuantityDisplay(line.productId, animate);
			});
		});
	},

	_onCardSelect(input, animateQuantity = false) {
		const card = input.closest(this.config.selectors.card);
		const stepId = input.name.replace('pack-step-', '');
		const stepState = this.state.steps[stepId];
		if (!stepState) return;

		const selection = {
			variantId: input.dataset.variantId,
			productId: card.dataset.productId,
			price: this._toEuros(input.dataset.price),
			compareAtPrice: this._toEuros(input.dataset.compareAtPrice),
			marketPrice: this._toEuros(input.dataset.marketPrice),
			quantity: 1,
			variantResolved: true,
		};

		if (input.type === 'radio') {
			stepState.lines = [selection];
		} else {
			const existingIndex = stepState.lines.findIndex((l) => l.variantId === selection.variantId);
			if (input.checked) {
				stepState.lines.push(selection);
			} else if (existingIndex > -1) {
				stepState.lines.splice(existingIndex, 1);
			}
			this._updateQuantityDisplay(selection.productId, animateQuantity);
		}

		// Changer de PRODUIT en step_1 (pas juste de taille sur le même produit,
		// cf. _onVariantChange) doit aussi recalculer la référence de taille —
		// sinon le matching reste figé sur l'ancien matelas si l'acheteur choisit
		// une autre carte plutôt qu'une autre valeur de picker sur la même carte.
		if (stepId === 'step_1' && input.checked) {
			this._updateSizeReferenceFromProduct(selection.productId, selection.variantId);
			this._updateGallery(selection.productId);
		}

		this._updateStepThumbnail(stepId);
		this._updateSubmitState();
		this._updateRecap();

		const lastLine = stepState.lines[stepState.lines.length - 1];
		window.emit('global:pack-selection-changed', { stepId, productId: lastLine?.productId || null });

		// Le picker (frère de la carte) redevient visible via CSS : refresh()
		// son swatch Splide au cas où il aurait monté à largeur 0.
		const stepProduct = card.closest(this.config.selectors.stepProduct);
		stepProduct?.querySelectorAll(this.config.selectors.variantSwatch).forEach((swatch) => {
			swatch._splide?.refresh();
		});
	},

	// Radio toujours à 1 ligne (checked forcé par le navigateur) — un simple
	// lines.length couvre les deux input_type sans distinguer radio/checkbox.
	// Image = dernier produit coché, sinon fallback metaobject (<template>).
	_updateStepThumbnail(stepId) {
		const thumbnail = this.root.querySelector(`[data-pack-step-thumbnail="${stepId}"]`);
		if (!thumbnail) return;
		const lines = this.state.steps[stepId].lines;
		thumbnail.classList.toggle('is-active', lines.length > 0);

		const image = thumbnail.querySelector(this.config.selectors.stepThumbnailImage);
		const lastLine = lines[lines.length - 1];
		const cardImage = lastLine && this.root.querySelector(
			`${this.config.selectors.accordion}[data-step="${stepId}"] ${this.config.selectors.card}[data-product-id="${lastLine.productId}"] ${this.config.selectors.cardImage}`
		);
		const source = cardImage || thumbnail.querySelector(this.config.selectors.stepThumbnailFallback)?.content.querySelector('img');
		if (!image || !source) return;

		['srcset', 'src', 'width', 'height', 'alt'].forEach((attr) => {
			const value = source.getAttribute(attr);
			if (value === null) image.removeAttribute(attr);
			else image.setAttribute(attr, value);
		});
	},

	_updateGallery(productId) {
		const { galleryMainSlider, galleryThumbnailSlider, galleryList, galleryArrows } = this.config.selectors;
		const sliders = [galleryMainSlider, galleryThumbnailSlider].map((selector) => this.root.querySelector(selector));
		const templates = sliders.map((slider) => slider?.querySelector(`template[data-pack-gallery-slides="${productId}"]`));
		if (templates.some((template) => !template)) return;

		const mediaSliders = window.ProductMediaSliders;
		const isMounted = Boolean(mediaSliders?.instances.main);
		if (isMounted) mediaSliders.destroy('main');

		sliders.forEach((slider, index) => {
			slider.querySelector(galleryList)?.replaceChildren(templates[index].content.cloneNode(true));
		});

		const [mainSlider] = sliders;
		const hasSeveralSlides = templates[0].content.children.length > 1;
		const arrows = mainSlider.querySelector(galleryArrows);
		arrows?.classList.toggle('mm-mobile', hasSeveralSlides);
		arrows?.classList.toggle('mm-none', !hasSeveralSlides);
		mainSlider.classList.add('is-moved');

		if (isMounted) mediaSliders.init('main', false);
	},

	/**
	 * Recomputes state.sizeMatching from a given product/variant — the
	 * variant currently selected on that card (its default variant right
	 * after being picked, before any picker click). Used when step 1's
	 * PRODUCT changes (_onCardSelect), mirroring what _onVariantChange does
	 * when only step 1's SIZE picker changes on the same product.
	 */
	_updateSizeReferenceFromProduct(productId, variantId) {
		const catalog = this.state.variantCatalog[productId];
		if (!catalog) return;

		const variant = catalog.variants.find((v) => String(v.id) === String(variantId));
		if (!variant) return;

		// Le sizeMap est indexé par valeur d'option (cf. data-pack-size-map),
		// pas par variant.id — il faut retrouver la valeur de l'option taille
		// portée par CETTE variante avant de pouvoir lire ses codes.
		let referenceSizeCodes = [];
		catalog.sizeMap.forEach((valueMap, position) => {
			const value = variant[`option${position}`];
			if (value && valueMap.has(value)) referenceSizeCodes = valueMap.get(value);
		});

		this.state.sizeMatching.referenceSizeCodes = referenceSizeCodes;
		this.state.sizeMatching.active = referenceSizeCodes.length > 0;

		window.log('info', '🛏️ [PACK_FUNNEL] [SIZE_DEBUG]', 'Step 1 product changed — new reference codes', {
			productId,
			variantId,
			referenceSizeCodes,
			active: this.state.sizeMatching.active,
		});

		this._reapplySizeFiltering();
	},

	/**
	 * Pose l'état disabled initial des boutons -/+ de chaque stepper présent
	 * dans le DOM (ex: "+" désactivé d'entrée si max_qty === 1), y compris sur
	 * les cartes non cochées — évite un flash d'un bouton actif qui deviendrait
	 * disabled seulement après un premier clic.
	 */
	_initQuantityDisplays() {
		this.root.querySelectorAll(this.config.selectors.quantityStepper).forEach((stepper) => {
			this._updateQuantityDisplay(stepper.dataset.productId);
		});
	},

	_updateQuantityDisplay(productId, animate = false) {
		const stepper = this.root.querySelector(`${this.config.selectors.quantityStepper}[data-product-id="${productId}"]`);
		const valueEl = stepper?.querySelector(this.config.selectors.quantityValue);
		if (!valueEl) return;

		const accordion = stepper.closest(this.config.selectors.accordion);
		const stepState = this.state.steps[accordion?.dataset.step];
		const line = stepState?.lines.find((l) => l.productId === productId);
		const quantity = line ? line.quantity : 0;
		const maxQty = Number(stepper.dataset.maxQty) || Infinity;

		clearTimeout(valueEl._tickTimer);
		if (animate && String(quantity) !== valueEl.textContent && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			// Le chiffre change à mi-animation, quand il est invisible
			valueEl.classList.remove('tick');
			void valueEl.offsetWidth;
			valueEl.classList.add('tick');
			valueEl._tickTimer = setTimeout(() => { valueEl.textContent = quantity; }, 120);
		} else {
			valueEl.textContent = quantity;
		}

		const decreaseBtn = stepper.querySelector(this.config.selectors.quantityDecrease);
		if (decreaseBtn) decreaseBtn.disabled = quantity <= 0;

		const increaseBtn = stepper.querySelector(this.config.selectors.quantityIncrease);
		if (increaseBtn) increaseBtn.disabled = quantity >= maxQty;
	},

	/**
	 * Quantité clampée à [0, maxQty] : le "-" à 1 décoche la carte (quantité 0,
	 * ligne retirée), bouton correspondant désactivé aux bornes (0 / maxQty)
	 * via _updateQuantityDisplay.
	 *
	 * Carte pas encore cochée : le stepper est visible mais sans effet tant
	 * qu'aucune ligne n'existe pour ce produit (rien à incrémenter). Cliquer
	 * sur +/- dans cet état coche d'abord la carte (quantité 1, même chemin
	 * que _onCardSelect) plutôt que de rester silencieusement inerte — bug UX
	 * constaté en prod : l'utilisateur pensait ajouter le produit en cliquant
	 * sur + et rien ne se passait.
	 */
	_onQuantityChange(stepper, delta) {
		const productId = stepper.dataset.productId;
		const accordion = stepper.closest(this.config.selectors.accordion);
		const stepState = this.state.steps[accordion?.dataset.step];
		if (!stepState) return;
		stepState.quantityTouched = true;

		let index = stepState.lines.findIndex((l) => l.productId === productId);
		if (index === -1) {
			const card = stepper.closest(this.config.selectors.card);
			const input = card?.querySelector('input');
			if (!input || input.checked || input.disabled) return;
			input.checked = true;
			this._onCardSelect(input, true);
			return;
		}

		const maxQty = Number(stepper.dataset.maxQty) || Infinity;
		const nextQuantity = stepState.lines[index].quantity + delta;
		if (nextQuantity < 1) {
			const input = stepper.closest(this.config.selectors.card)?.querySelector('input');
			if (!input) return;
			input.checked = false;
			this._onCardSelect(input, true);
			return;
		}
		stepState.lines[index].quantity = Math.min(nextQuantity, maxQty);

		this._updateQuantityDisplay(productId, true);
		this._updateRecap();
	},

	_collectSelections() {
		const selections = [];
		this.state.stepOrder.forEach((stepId) => {
			this.state.steps[stepId].lines.forEach((line) => selections.push({ ...line }));
		});

		return selections;
	},

	// Deux instances du récap dans le DOM (flux + barre fixed mobile, cf.
	// MM-Product-Pack-Funnel.liquid) : chaque data-pack-recap-* est mis à jour
	// via querySelectorAll/forEach, jamais getElement (ne toucherait que la
	// 1ère occurrence et laisserait la seconde désynchronisée).
	_updateRecap() {
		// Ligne en rupture (variantResolved false) hors prix, comme pack_price
		// côté Liquid : son price est celui d'une variante non achetable.
		const isPriced = (line) => line.variantResolved !== false;
		const selections = this._collectSelections().filter(isPriced);
		const totalUnits = selections.reduce((sum, s) => sum + s.quantity, 0);
		const total = selections.reduce((sum, s) => sum + s.price * s.quantity, 0);

		const format = (value) => this._formatMoney(value);

		window.getElements(this.config.selectors.recapSubtotalLabel).forEach((subtotalLabelEl) => {
			const subtotalTemplate = subtotalLabelEl.dataset.subtotalTemplate;
			if (subtotalTemplate) subtotalLabelEl.textContent = subtotalTemplate.replace('__COUNT__', totalUnits);
		});

		// Économie pack = (Σ marketPrice des étapes + compareAtPrice du produit
		// offert) − Σ price. Une ligne sans prix de référence supérieur au prix
		// n'ajoute rien (même règle que l'affichage carte). Le sous-total affiche
		// la valeur de référence (total + économie), le total net ce qui est payé.
		const savings = this.state.stepOrder.reduce((sum, stepId) => {
			return this.state.steps[stepId].lines.filter(isPriced).reduce((stepSum, line) => {
				const referencePrice = stepId === 'step_gift' ? line.compareAtPrice : line.marketPrice;
				return stepSum + Math.max(0, referencePrice - line.price) * line.quantity;
			}, sum);
		}, 0);

		window.getElements(this.config.selectors.recapSubtotal).forEach((subtotalEl) => {
			subtotalEl.textContent = format(total + savings);
		});

		window.getElements(this.config.selectors.recapSavingsRow).forEach((savingsRowEl) => {
			savingsRowEl.hidden = savings <= 0;
			const savingsEl = savingsRowEl.querySelector(`${this.config.selectors.recapSavings} .mm-label-text`);
			if (savingsEl) savingsEl.textContent = `-${format(savings)}`;
		});

		window.getElements(this.config.selectors.recapNet).forEach((netEl) => {
			netEl.textContent = format(total);
		});

		window.getElements(this.config.selectors.recapCtaPrice).forEach((ctaPriceEl) => {
			ctaPriceEl.textContent = format(total);
		});

		this._updateHeaderPrice(total, savings);
	},

	_formatMoney(value, withCents = true) {
		const formatter = withCents ? this.config.currencyFormatter : this.config.currencyFormatterNoCents;
		return formatter.format(value).replace(/\u202f/g, '\u00a0');
	},

	_formatPriceWithoutTrailingZeros(value) {
		const hasCents = Math.round(value * 100) % 100 !== 0;
		return this._formatMoney(value, hasCents);
	},

	_updateHeaderPrice(total, savings) {
		const s = this.config.selectors;
		const referencePrice = total + savings;
		const hasSavings = savings > 0;
		const percentage = hasSavings ? Math.round((savings / referencePrice) * 100) : 0;

		window.getElements(s.headerPrice).forEach((priceEl) => {
			priceEl.textContent = this._formatPriceWithoutTrailingZeros(total);
		});

		window.getElements(s.headerMarket).forEach((marketEl) => {
			marketEl.hidden = !hasSavings;
			const marketPriceEl = marketEl.querySelector(s.headerMarketPrice);
			if (marketPriceEl) marketPriceEl.textContent = this._formatPriceWithoutTrailingZeros(referencePrice);
		});

		window.getElements(s.headerPercentage).forEach((percentageEl) => {
			percentageEl.hidden = !hasSavings;
			const percentageTemplate = percentageEl.dataset.percentageTemplate;
			const percentageTextEl = percentageEl.querySelector('.mm-label-text');
			if (percentageTextEl && percentageTemplate) percentageTextEl.textContent = percentageTemplate.replace('__PERCENTAGE__', percentage);
		});

		window.getElements(s.headerInstallments).forEach((installmentsEl) => {
			const count = Number(installmentsEl.dataset.installmentsCount);
			const template = installmentsEl.dataset.installmentsTemplate;
			if (!(count > 1) || !template) return;
			const amount = Math.floor(Math.round(total * 100) / count) / 100;
			installmentsEl.textContent = template.replace('[installments]', `${count} x ${this._formatPriceWithoutTrailingZeros(amount)}`);
		});
	},

	/**
	 * POST unique /cart/add.js avec items[] — pas de cart/clear.js, le pack
	 * s'ajoute au panier existant.
	 * - _pack_id : handle du pack (identifie le type — sert au rendu cart)
	 * - _pack_uid : unique par ajout (distingue 2 instances du même pack au panier)
	 * - [packLabel]: packTitle : property VISIBLE au checkout (ex "Votre pack: Pack Sommeil")
	 */
	async _addPackToCart() {
		if (!this.state.canSubmit) return;
		const selections = this._collectSelections();
		const packUid = `${this.state.packId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
		const baseProperties = {
			_pack_id: this.state.packId,
			_pack_uid: packUid,
		};
		if (this.state.packLabel && this.state.packTitle) {
			baseProperties[this.state.packLabel] = this.state.packTitle;
		}
		const items = selections
			.filter((s) => s.variantId)
			.map((s) => ({
				id: s.variantId,
				quantity: s.quantity,
				properties: { ...baseProperties },
			}));

		if (!items.length || typeof window.CartAPI === 'undefined') {
			window.log('error', '🛏️ [PACK_FUNNEL]', 'Add to cart aborted: no items or CartAPI unavailable');
			return;
		}

		try {
			const response = await window.CartAPI.request('POST', 'cart/add.js', { items });
			// Emit cart:add-to-cart-success pour declencher le toast MM (mm_cart_toast.js
			// ecoute cet event et lit e.detail.data.response.items pour afficher le dernier item).
			// CartAPI.request n'emet cet event que sur les submits de <form>, pas sur les fetch
			// directs comme celui du pack funnel — d'ou l'emission manuelle ici.
			document.dispatchEvent(new CustomEvent('cart:add-to-cart-success', {
				detail: {
					data: {
						response,
						items,
						source: 'pack-funnel',
						packUid,
						variantId: items[items.length - 1]?.id,
						quantity: items[items.length - 1]?.quantity || 1,
						properties: baseProperties,
					},
				},
			}));
			window.log('info', '🛏️ [PACK_FUNNEL]', 'Pack added to cart', { items });
		} catch (error) {
			window.log('error', '🛏️ [PACK_FUNNEL]', 'Add pack to cart failed', { error: error.message });
		}
	},
};

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => PackFunnelManager.init());
} else {
	PackFunnelManager.init();
}
